import ModuleBuilder from './ModuleBuilder';
import FunctionBuilder from './FunctionBuilder';
import ImportBuilder from './ImportBuilder';
import GlobalBuilder from './GlobalBuilder';
import { ExternalKind, HeapTypeRef, ImmediateType, ValueTypeDescriptor } from './types';
import Immediate from './Immediate';
import FuncTypeBuilder from './FuncTypeBuilder';
import StructTypeBuilder from './StructTypeBuilder';
import ArrayTypeBuilder from './ArrayTypeBuilder';
import RecGroupBuilder from './RecGroupBuilder';
import GlobalType from './GlobalType';
import MemoryType from './MemoryType';
import TableType from './TableType';
import Instruction from './Instruction';
import FunctionParameterBuilder from './FunctionParameterBuilder';
import LocalBuilder from './LocalBuilder';

function formatHeapTypeRef(heapType: HeapTypeRef): string {
  if (typeof heapType === 'number') {
    return String(heapType);
  }
  if ('name' in heapType && typeof heapType.name === 'string') {
    return heapType.name;
  }
  if ('index' in heapType && typeof heapType.index === 'number') {
    return String(heapType.index);
  }
  return '';
}

export default class TextModuleWriter {
  moduleBuilder: ModuleBuilder;

  constructor(moduleBuilder: ModuleBuilder) {
    this.moduleBuilder = moduleBuilder;
  }

  toString(): string {
    const lines: string[] = [];
    const mod = this.moduleBuilder;

    lines.push(`(module $${mod._name}`);

    this.writeTypes(lines, mod);
    this.writeImports(lines, mod);
    this.writeFunctions(lines, mod);
    this.writeTables(lines, mod);
    this.writeMemories(lines, mod);
    this.writeGlobals(lines, mod);
    this.writeTags(lines, mod);
    this.writeExports(lines, mod);
    this.writeStart(lines, mod);
    this.writeElements(lines, mod);
    this.writeData(lines, mod);

    lines.push(')');
    return lines.join('\n');
  }

  private formatType(type: FuncTypeBuilder | StructTypeBuilder | ArrayTypeBuilder, index: number): string {
    if (type instanceof FuncTypeBuilder) {
      const params = type.parameterTypes.map((p: ValueTypeDescriptor) => p.name).join(' ');
      const results = type.returnTypes.map((r: ValueTypeDescriptor) => r.name).join(' ');
      let sig = `(func`;
      if (params.length > 0) sig += ` (param ${params})`;
      if (results.length > 0) sig += ` (result ${results})`;
      sig += ')';
      return `(type (;${index};) ${sig})`;
    } else if (type instanceof StructTypeBuilder) {
      const fields = type.fields.map((f) => {
        const mut = f.mutable ? `(mut ${f.type.name})` : f.type.name;
        return `(field $${f.name} ${mut})`;
      }).join(' ');
      return `(type (;${index};) (struct ${fields}))`;
    } else {
      const mut = type.mutable ? `(mut ${type.elementType.name})` : type.elementType.name;
      return `(type (;${index};) (array ${mut}))`;
    }
  }

  private writeTypes(lines: string[], mod: ModuleBuilder): void {
    let typeIndex = 0;
    for (const entry of mod._typeSectionEntries) {
      if (entry instanceof RecGroupBuilder) {
        if (entry._types.length === 1) {
          lines.push(`  ${this.formatType(entry._types[0], typeIndex)}`);
          typeIndex++;
        } else {
          const inner = entry._types.map((t, i) =>
            `    ${this.formatType(t, typeIndex + i)}`
          ).join('\n');
          lines.push(`  (rec\n${inner}\n  )`);
          typeIndex += entry._types.length;
        }
      } else {
        lines.push(`  ${this.formatType(entry, typeIndex)}`);
        typeIndex++;
      }
    }
  }

  private writeImports(lines: string[], mod: ModuleBuilder): void {
    mod._imports.forEach((imp, i) => {
      let desc = '';
      switch (imp.externalKind) {
        case ExternalKind.Function: {
          const funcType = imp.data as FuncTypeBuilder;
          desc = `(func (;${imp.index};) (type ${funcType.index}))`;
          break;
        }
        case ExternalKind.Table: {
          const tableType = imp.data as TableType;
          const limits = tableType.resizableLimits;
          const max = limits.maximum !== null ? ` ${limits.maximum}` : '';
          desc = `(table (;${imp.index};) ${limits.initial}${max} ${tableType.elementType.name})`;
          break;
        }
        case ExternalKind.Memory: {
          const memType = imp.data as MemoryType;
          const limits = memType.resizableLimits;
          const max = limits.maximum !== null ? ` ${limits.maximum}` : '';
          const shared = memType.shared ? ' shared' : '';
          const m64 = memType.memory64 ? ' i64' : '';
          desc = `(memory (;${imp.index};)${m64} ${limits.initial}${max}${shared})`;
          break;
        }
        case ExternalKind.Global: {
          const globalType = imp.data as GlobalType;
          const valType = globalType.valueType.name;
          desc = globalType.mutable
            ? `(global (;${imp.index};) (mut ${valType}))`
            : `(global (;${imp.index};) ${valType})`;
          break;
        }
        case ExternalKind.Tag: {
          const tagFuncType = imp.data as FuncTypeBuilder;
          const tagParams = tagFuncType.parameterTypes.map((p: ValueTypeDescriptor) => p.name).join(' ');
          let tagSig = '';
          if (tagParams.length > 0) tagSig = ` (param ${tagParams})`;
          desc = `(tag (;${imp.index};) (type ${tagFuncType.index})${tagSig})`;
          break;
        }
      }
      lines.push(`  (import "${imp.moduleName}" "${imp.fieldName}" ${desc})`);
    });
  }

  private writeFunctions(lines: string[], mod: ModuleBuilder): void {
    mod._functions.forEach((func) => {
      const typeIdx = func.funcTypeBuilder.index;
      let header = `  (func $${func.name} (;${func._index};) (type ${typeIdx})`;

      if (func.funcTypeBuilder.parameterTypes.length > 0) {
        const params = func.funcTypeBuilder.parameterTypes
          .map((p, i) => {
            const param = func.parameters[i];
            return p.name;
          })
          .join(' ');
        header += ` (param ${params})`;
      }

      if (func.funcTypeBuilder.returnTypes.length > 0) {
        const results = func.funcTypeBuilder.returnTypes.map((r) => r.name).join(' ');
        header += ` (result ${results})`;
      }

      if (!func.functionEmitter) {
        lines.push(header + ')');
        return;
      }

      const emitter = func.functionEmitter;

      // Locals
      if (emitter._locals.length > 0) {
        const locals = emitter._locals.map((l) => {
          if (l.count === 1) return `(local ${l.valueType.name})`;
          return Array(l.count).fill(`(local ${l.valueType.name})`).join(' ');
        });
        header += ' ' + locals.join(' ');
      }

      lines.push(header);

      // Instructions
      this.writeInstructions(lines, emitter._instructions, 2);
      lines.push('  )');
    });
  }

  private writeInstructions(lines: string[], instructions: Instruction[], baseIndent: number): void {
    let indent = baseIndent;

    for (const instr of instructions) {
      const mnemonic = instr.opCode.mnemonic;

      // Decrease indent before end/else/catch/catch_all/delegate
      if (mnemonic === 'end' || mnemonic === 'else' || mnemonic === 'catch' || mnemonic === 'catch_all' || mnemonic === 'delegate') {
        indent = Math.max(baseIndent, indent - 1);
      }

      const prefix = '  '.repeat(indent);
      let line = `${prefix}${mnemonic}`;

      // Add immediate operand text
      if (instr.immediate) {
        const immText = this.immediateToText(instr.immediate);
        if (immText) {
          line += ` ${immText}`;
        }
      }

      lines.push(line);

      // Increase indent after block/loop/if/else/try/catch/catch_all
      if (mnemonic === 'block' || mnemonic === 'loop' || mnemonic === 'if' || mnemonic === 'else' || mnemonic === 'try' || mnemonic === 'catch' || mnemonic === 'catch_all') {
        indent++;
      }
    }
  }

  private immediateToText(immediate: Immediate): string {
    const operands = immediate.operands;
    switch (operands.type) {
      case ImmediateType.BlockSignature: {
        const blockType = operands.blockType;
        if (typeof blockType === 'number') {
          return `(type ${blockType})`;
        }
        if (blockType && blockType.name !== 'void') {
          return `(result ${blockType.name})`;
        }
        return '';
      }
      case ImmediateType.VarInt32:
      case ImmediateType.VarInt64:
      case ImmediateType.Float32:
      case ImmediateType.Float64:
      case ImmediateType.VarUInt1:
      case ImmediateType.VarUInt32:
        return String(operands.value);
      case ImmediateType.Local:
        return String(operands.local.index);
      case ImmediateType.Global: {
        const global = operands.global;
        if (global instanceof GlobalBuilder) {
          return String(global._index);
        }
        if (global instanceof ImportBuilder) {
          return String(global.index);
        }
        return '';
      }
      case ImmediateType.Function: {
        const func = operands.func;
        if (func instanceof FunctionBuilder) {
          return String(func._index);
        }
        if (func instanceof ImportBuilder) {
          return String(func.index);
        }
        return '';
      }
      case ImmediateType.IndirectFunction: {
        let text = '';
        if (operands.tableIndex > 0) text += `${operands.tableIndex} `;
        text += `(type ${operands.funcType.index})`;
        return text;
      }
      case ImmediateType.RelativeDepth:
        return operands.label.block
          ? String(operands.depth - operands.label.block.depth)
          : String(operands.depth);
      case ImmediateType.BranchTable:
        return operands.relativeDepths.join(' ') + ' ' + operands.defaultDepth;
      case ImmediateType.MemoryImmediate: {
        let text = '';
        if (operands.offset !== 0) text += `offset=${operands.offset}`;
        if (operands.alignment !== 0) {
          if (text) text += ' ';
          text += `align=${1 << operands.alignment}`;
        }
        return text;
      }
      case ImmediateType.TypeIndexField:
        return `${operands.typeIndex} ${operands.fieldIndex}`;
      case ImmediateType.TypeIndexIndex:
        return `${operands.typeIndex} ${operands.index}`;
      case ImmediateType.HeapType:
        return formatHeapTypeRef(operands.heapType);
      case ImmediateType.BrOnCast: {
        const labelDepth = operands.label.block
          ? operands.depth - operands.label.block.depth
          : operands.depth;
        return `${labelDepth} ${formatHeapTypeRef(operands.heapType1)} ${formatHeapTypeRef(operands.heapType2)}`;
      }
      case ImmediateType.V128Const:
      case ImmediateType.LaneIndex:
      case ImmediateType.ShuffleMask:
        return '';
    }
  }

  private writeTables(lines: string[], mod: ModuleBuilder): void {
    mod._tables.forEach((table, i) => {
      const limits = table.resizableLimits;
      const max = limits.maximum !== null ? ` ${limits.maximum}` : '';
      lines.push(`  (table (;${table._index};) ${limits.initial}${max} ${table.elementType.name})`);
    });
  }

  private writeMemories(lines: string[], mod: ModuleBuilder): void {
    mod._memories.forEach((mem) => {
      const limits = mem._memoryType.resizableLimits;
      const max = limits.maximum !== null ? ` ${limits.maximum}` : '';
      const shared = mem._memoryType.shared ? ' shared' : '';
      const m64 = mem._memoryType.memory64 ? ' i64' : '';
      lines.push(`  (memory (;${mem._index};)${m64} ${limits.initial}${max}${shared})`);
    });
  }

  private writeGlobals(lines: string[], mod: ModuleBuilder): void {
    mod._globals.forEach((g) => {
      const valType = g.globalType.valueType.name;
      const typeStr = g.globalType.mutable ? `(mut ${valType})` : valType;

      let initExpr = '';
      if (g._initExpressionEmitter) {
        initExpr = this.formatInitExpr(g._initExpressionEmitter._instructions);
      }

      lines.push(`  (global (;${g._index};) ${typeStr} (${initExpr}))`);
    });
  }

  private writeTags(lines: string[], mod: ModuleBuilder): void {
    mod._tags.forEach((tag, i) => {
      const params = tag._funcType.parameterTypes.map((p) => p.name).join(' ');
      let sig = '';
      if (params.length > 0) sig = ` (param ${params})`;
      lines.push(`  (tag (;${i};) (type ${tag._funcType.index})${sig})`);
    });
  }

  private writeExports(lines: string[], mod: ModuleBuilder): void {
    mod._exports.forEach((exp) => {
      let kindName = '';
      let index = exp.data._index;
      switch (exp.externalKind) {
        case ExternalKind.Function:
          kindName = 'func';
          break;
        case ExternalKind.Table:
          kindName = 'table';
          break;
        case ExternalKind.Memory:
          kindName = 'memory';
          break;
        case ExternalKind.Global:
          kindName = 'global';
          break;
        case ExternalKind.Tag:
          kindName = 'tag';
          break;
      }
      lines.push(`  (export "${exp.name}" (${kindName} ${index}))`);
    });
  }

  private writeStart(lines: string[], mod: ModuleBuilder): void {
    if (mod._startFunction) {
      lines.push(`  (start ${mod._startFunction._index})`);
    }
  }

  private writeElements(lines: string[], mod: ModuleBuilder): void {
    mod._elements.forEach((elem, i) => {
      const funcIndices = elem._functions
        .map((f) => {
          if (f instanceof FunctionBuilder) return f._index;
          if (f instanceof ImportBuilder) return f.index;
          return 0;
        })
        .join(' ');

      if (elem._passive) {
        lines.push(`  (elem (;${i};) func ${funcIndices})`);
        return;
      }

      let offsetExpr = '';
      if (elem._initExpressionEmitter) {
        offsetExpr = this.formatInitExpr(elem._initExpressionEmitter._instructions);
      }

      const tableIndex = elem._table ? elem._table._index : 0;
      if (tableIndex !== 0) {
        lines.push(`  (elem (;${i};) (table ${tableIndex}) (${offsetExpr}) func ${funcIndices})`);
      } else {
        lines.push(`  (elem (;${i};) (${offsetExpr}) func ${funcIndices})`);
      }
    });
  }

  private writeData(lines: string[], mod: ModuleBuilder): void {
    mod._data.forEach((seg, i) => {
      const dataStr = this.bytesToWatString(seg._data);

      if (seg._passive) {
        lines.push(`  (data (;${i};) "${dataStr}")`);
        return;
      }

      let offsetExpr = '';
      if (seg._initExpressionEmitter) {
        offsetExpr = this.formatInitExpr(seg._initExpressionEmitter._instructions);
      }

      if (seg._memoryIndex !== 0) {
        lines.push(`  (data (;${i};) (memory ${seg._memoryIndex}) (${offsetExpr}) "${dataStr}")`);
      } else {
        lines.push(`  (data (;${i};) (${offsetExpr}) "${dataStr}")`);
      }
    });
  }

  private formatInitExpr(instructions: Instruction[]): string {
    const parts: string[] = [];
    for (const instr of instructions) {
      if (instr.opCode.mnemonic === 'end') continue;
      let part = instr.opCode.mnemonic;
      if (instr.immediate) {
        const immText = this.immediateToText(instr.immediate);
        if (immText) part += ` ${immText}`;
      }
      parts.push(part);
    }
    return parts.join(' ');
  }

  private bytesToWatString(data: Uint8Array): string {
    let result = '';
    for (let i = 0; i < data.length; i++) {
      const byte = data[i];
      if (byte >= 0x20 && byte < 0x7f && byte !== 0x22 && byte !== 0x5c) {
        result += String.fromCharCode(byte);
      } else {
        result += '\\' + byte.toString(16).padStart(2, '0');
      }
    }
    return result;
  }
}
